// 📄 pages/Proyectos/components/ResumenHorasCliente.tsx
// Vista de "Mis Horas" para el rol Cliente: a diferencia de HorasSection
// (que muestra solo los registros propios del usuario, con tarifa y
// total facturado), acá se ve el resumen de TODOS los consultores del
// proyecto, sin tarifa ni total en dinero — información confidencial de
// facturación que este rol no debe ver (ver resumenHorasService.ts).
//
// El cliente además puede Aceptar/Objetar cada registro. Esa escritura
// va directo a Firestore (no por onehubApi): las reglas ya acotan el
// update a los 3 campos de aprobación, así que no hace falta un
// endpoint nuevo — ver aprobarRegistro/objetarRegistro en
// registroHorasService.ts.
import { useEffect, useState } from 'react';
import { Clock, RefreshCw, Check, X } from 'lucide-react';
import { useProject } from '../../../context/ProjectContext.tsx';
import { obtenerResumenHoras, RegistroHorasResumen } from '../../../services/resumenHorasService.ts';
import { aprobarRegistro, objetarRegistro } from '../../../services/registroHorasService.ts';
import { ESTADO_APROBACION_STYLES } from './horasStyles.ts';
import { SOLUCIONES } from '../solucionesConfig.ts';

export default function ResumenHorasCliente() {
  const { projectId, project } = useProject();
  const [registros, setRegistros] = useState<RegistroHorasResumen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workingId, setWorkingId] = useState<string | null>(null);
  const [objetandoId, setObjetandoId] = useState<string | null>(null);
  const [comentarioInput, setComentarioInput] = useState('');

  const cargar = () => {
    if (!projectId) return;
    setLoading(true);
    obtenerResumenHoras(projectId)
      .then(setRegistros)
      .catch((err) => {
        console.error('Error cargando resumen de horas:', err);
        setError(err.message || 'No se pudo cargar el resumen de horas.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(cargar, [projectId]);

  const nombreConsultor = (uid: string): string => {
    const miembro = project?.miembros.find(m => m.userId === uid);
    return miembro?.email || 'Consultor desconocido';
  };

  const handleAceptar = async (registroId: string) => {
    setWorkingId(registroId);
    try {
      await aprobarRegistro(projectId, registroId);
      cargar();
    } catch (err: any) {
      console.error('Error aceptando registro:', err);
      alert(err.message || 'Error al aceptar el registro.');
    } finally {
      setWorkingId(null);
    }
  };

  const handleAbrirObjetar = (registroId: string) => {
    setObjetandoId(registroId);
    setComentarioInput('');
  };

  const handleConfirmarObjetar = async (registroId: string) => {
    setWorkingId(registroId);
    try {
      await objetarRegistro(projectId, registroId, comentarioInput);
      setObjetandoId(null);
      setComentarioInput('');
      cargar();
    } catch (err: any) {
      console.error('Error objetando registro:', err);
      alert(err.message || 'Error al objetar el registro.');
    } finally {
      setWorkingId(null);
    }
  };

  const totalHoras = registros.reduce((acc, r) => acc + r.horas, 0);

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <p className="text-sm">Cargando resumen de horas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 max-w-xs">
        <p className="text-xs text-gray-400 mb-1">Total de horas registradas</p>
        <p className="text-2xl font-light text-white">{totalHoras.toLocaleString('es', { maximumFractionDigits: 2 })}</p>
      </div>

      {registros.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Clock className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Todavía no hay horas registradas en este proyecto.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {registros.map((r) => {
            const fecha = r.fecha ? new Date(r.fecha) : null;
            const trabajando = workingId === r.id;
            return (
              <div
                key={r.id}
                className={`flex items-start gap-4 bg-white/5 border rounded-2xl p-4 ${
                  r.estadoAprobacion === 'Objetado' ? 'border-red-500/40' : 'border-white/10'
                }`}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-[rgba(91,155,240,0.12)]">
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="flex items-center gap-3 flex-wrap text-xs text-gray-400">
                    <span className="text-sm font-medium text-white">{fecha ? fecha.toLocaleDateString() : '—'}</span>
                    <span className="text-gray-600">&middot;</span>
                    <span>{SOLUCIONES[r.solucionRelacionada]?.label || r.solucionRelacionada}</span>
                    <span className="text-gray-600">&middot;</span>
                    <span>{r.horas.toLocaleString('es', { maximumFractionDigits: 2 })} h</span>
                    <span className="text-gray-600">&middot;</span>
                    <span className="text-indigo-300">{nombreConsultor(r.creadoPor)}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${ESTADO_APROBACION_STYLES[r.estadoAprobacion]}`}>
                      {r.estadoAprobacion}
                    </span>
                  </div>
                  {r.cambiosRealizados && (
                    <p className="text-xs text-gray-500">{r.cambiosRealizados}</p>
                  )}
                  {r.estadoAprobacion === 'Objetado' && r.comentarioCliente && (
                    <p className="text-xs text-red-400">Objeción: {r.comentarioCliente}</p>
                  )}

                  {objetandoId === r.id ? (
                    <div className="flex items-center gap-2 pt-1">
                      <input
                        autoFocus
                        type="text"
                        value={comentarioInput}
                        onChange={(e) => setComentarioInput(e.target.value)}
                        placeholder="Motivo de la objeción..."
                        className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-red-500"
                      />
                      <button
                        onClick={() => handleConfirmarObjetar(r.id)}
                        disabled={trabajando || !comentarioInput.trim()}
                        className="text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-all disabled:opacity-40"
                      >
                        Confirmar
                      </button>
                      <button
                        onClick={() => setObjetandoId(null)}
                        disabled={trabajando}
                        className="text-[11px] px-3 py-1.5 rounded-lg text-gray-400 hover:bg-white/10 transition-all disabled:opacity-40"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        onClick={() => handleAceptar(r.id)}
                        disabled={trabajando}
                        className="flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 transition-all disabled:opacity-40"
                      >
                        <Check className="w-3 h-3" /> Aceptar
                      </button>
                      <button
                        onClick={() => handleAbrirObjetar(r.id)}
                        disabled={trabajando}
                        className="flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-all disabled:opacity-40"
                      >
                        <X className="w-3 h-3" /> Objetar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
